import type { InteractionFamily, ScaleCell, ScaleConfig } from 'Types/ExerciseContentTypes';
import type { StepwiseRunPrompt } from 'Types/ExerciseRecipeTypes';
import type { SessionExercise } from 'Phaser/ExerciseSessionMachine/exerciseSessionMachine.types';
import type { PrePracticeTier } from './prePracticeReadModel';
import { getExercisePrePracticeReadModel } from './prePractice.fixture';

/**
 * Resolve a tapped (level, tier) on the Exercises overview into the concrete
 * sitting the play scene needs (MUS-481). This is the minimal selection -> session
 * transition: no hand/octave/tempo chooser, just the tapped level's representative
 * cell launched at the tapped tier's waypoint. The richer per-level pre-session
 * surface is a later-phase screen; here the tap itself is the whole transition.
 *
 * Source seams:
 *   - The overview STRUCTURE (levels / tiers / cells) is the same read model the
 *     pre-practice screen renders (`getExercisePrePracticeReadModel`), so the play
 *     route resolves the tap against exactly what the user saw. When the read
 *     endpoint replaces the fixture, this resolver inherits it with no change.
 *   - A level's `cells` are its (scale, tonic) coordinates. The representative cell
 *     is the first - `level.cells[0]`. Every slice cell is a major scale, so the one
 *     shared PROMPT below spells correctly for whichever tonic the level teaches:
 *     the expander derives spelling + key signature from the cell's tonic and only
 *     anchors register near the prompt's anchor, so the prompt is tonic-agnostic
 *     scaffolding.
 *   - A tier IS a full-config waypoint, so its fields map one-to-one onto the
 *     playable `ScaleConfig` (hand / octaves / direction / rhythm / tempo). Tapping
 *     the Beginner tier plays the Beginner waypoint; no config is invented.
 */

/**
 * The stepwise-run figure for the slice: a one-octave major-scale interval walk.
 * Tonic-agnostic - the cell drives spelling and key; this only carries the figure,
 * meter, register anchor, and single-cell traversal. Shared by the real play route
 * and the dev launcher so there is one definition, not two that can drift.
 */
export const PLAY_PROMPT: StepwiseRunPrompt = {
  figure: 'stepwise_run',
  intervals: [2, 2, 1, 2, 2, 2, 1],
  time_signature: [4, 4],
  register: {
    rh_anchor_by_range: { 1: 'C4', 2: 'C4', 3: 'C3', 4: 'C3' },
    lh_offset_octaves: -1,
  },
  traversal: { kind: 'single_cell' },
};

/**
 * The same figure laid out as one long bar with the meter hidden: a one-octave
 * quarter-note scale is eight notes, so it fills exactly one 8/4 bar. Time
 * signatures in exercises are a layout tool, so this is the shape a scale may
 * take. The dev launcher plays it at `/__visual/exercise-play?meter=one-bar`,
 * and the scene specs pin it; the real play route stays on PLAY_PROMPT.
 */
export const ONE_BAR_PLAY_PROMPT: StepwiseRunPrompt = {
  ...PLAY_PROMPT,
  time_signature: [8, 4],
  show_time_signature: false,
};

/**
 * The session-facing identity of the one slice exercise ("Play Scales from
 * Notation"): id, slug, and the repeatability policies (clearance = cumulative 3,
 * mastery = streak 3). The saga attributes earned clears to this id.
 */
export const PLAY_EXERCISE: SessionExercise = {
  id: 1,
  slug: 'scales-performance-read',
  clearance_policy: { type: 'cumulative', n: 3 },
  mastery_policy: { type: 'streak', n: 3 },
};

/** The concrete sitting the scene launches: figure + exercise identity + the
 *  content coordinate (cell) + the frontier configuration in play (config) + the
 *  interaction family that binds it to a playable scene (MUS-456). */
export interface ResolvedPlaySession {
  prompt: StepwiseRunPrompt;
  exercise: SessionExercise;
  cell: ScaleCell;
  config: ScaleConfig;
  interaction_family: InteractionFamily;
}

/**
 * The interaction family of the slice's one exercise ("Play Scales from Notation").
 * It is a performed-family exercise, so the scene-config binding (MUS-456) resolves
 * it to `OSMDExerciseScene`. When the read endpoint replaces the fixture this comes
 * from the package's own `interaction_family` column; here the single slice exercise
 * carries it as the shared constant.
 */
export const PLAY_INTERACTION_FAMILY: InteractionFamily = 'performed';

/** Map a tier waypoint to the playable frontier configuration (one-to-one). */
function tierToConfig(tier: PrePracticeTier): ScaleConfig {
  return {
    hand: tier.hand,
    range_octaves: tier.rangeOctaves,
    direction: tier.direction,
    rhythm_density: tier.rhythmDensity,
    tempo: tier.tempo,
  };
}

/**
 * Resolve a tapped level (and optional tier) into a playable sitting, or `null`
 * when the coordinates do not resolve (unknown level, or a level with no cells).
 *
 * `tierNumber` is optional: when omitted, or when the requested tier is not found,
 * the level's lowest (first) tier is used - the minimal default entry. This keeps
 * bad or partial route params from throwing; they degrade to the level's easiest
 * waypoint rather than a crash.
 */
export function resolvePlaySession(
  levelNumber: number,
  tierNumber?: number,
): ResolvedPlaySession | null {
  const readModel = getExercisePrePracticeReadModel();
  const level = readModel.levels.find((lv) => lv.levelNumber === levelNumber);
  if (!level) return null;

  const cell = level.cells[0];
  if (!cell) return null;

  const requested =
    tierNumber != null ? level.tiers.find((t) => t.tierNumber === tierNumber) : undefined;
  const tier = requested ?? level.tiers[0];
  if (!tier) return null;

  return {
    prompt: PLAY_PROMPT,
    exercise: PLAY_EXERCISE,
    cell,
    config: tierToConfig(tier),
    interaction_family: PLAY_INTERACTION_FAMILY,
  };
}
